import type { ReactNode } from 'react';
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Clock,
  Search as SearchIcon,
  FileText,
  Lightbulb,
  Database,
  RefreshCw,
} from 'lucide-react';
import type {
  ArticleEffectiveness,
  EmbeddingStatus,
  FailedSearch,
  PopularSearch,
} from '@services/knowledge-analytics.service';
import { CARD, CARD_CLASS, CardFoot } from './shell';
import {
  clickRatePct,
  embedHealth,
  embedPriority,
  embedState,
  embedTally,
  helpfulnessText,
} from './sectionFigures';
import { MetricCard } from './InsightsStats';
import { ThemedSelect } from '@components/common/ThemedSelect';

/** The health bar's colour. Three bands, the same ones the Overview uses. */
function healthTone(score: number): string {
  if (score > 90) return 'var(--co-green-ink)';
  if (score > 70) return 'var(--co-terracotta-ink)';
  return 'var(--co-burgundy)';
}

// The five Insights sections, behind the sub-tab strip the old Knowledge
// Analytics screen used.
//
// Each section answers one question and each row carries a verb or a fact
// somebody can act on. What is NOT here is the old screen's chart furniture —
// the donut and the two trend lines had no action attached to them, and the
// figures underneath them were the part anyone read.

const MONO = 'font-mono';

export type InsightsSection =
  | 'overview'
  | 'search'
  | 'articles'
  | 'gaps'
  | 'embeddings';

const SECTIONS: { id: InsightsSection; label: string; icon: typeof BarChart3 }[] = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'search', label: 'Search Analytics', icon: SearchIcon },
  { id: 'articles', label: 'Article Performance', icon: FileText },
  { id: 'gaps', label: 'Knowledge Gaps', icon: Lightbulb },
  { id: 'embeddings', label: 'Embeddings', icon: Database },
];

/**
 * The sections whose figures actually change with the reporting period.
 *
 * Only the Overview's dashboard request carries the period. Search, article
 * performance, the gap list and embeddings are fetched without a date range,
 * so a period selector on those sections would claim a filter that is not
 * applied — the reader takes all-time figures for "last 7 days". Add a section
 * here only once its requests send the period.
 */
const PERIOD_SECTIONS: ReadonlySet<InsightsSection> = new Set<InsightsSection>(['overview']);

export function sectionUsesPeriod(section: InsightsSection): boolean {
  return PERIOD_SECTIONS.has(section);
}

/**
 * The analytics header: title, subtitle, period and refresh.
 *
 * Above the strip, where the old screen put it. The period selector is shown
 * only when `showPeriod` is set — i.e. on sections the period applies to (see
 * `sectionUsesPeriod`). The chosen value is kept while it is hidden, so going
 * back to the Overview shows the same period the user left it on. Refresh is
 * always shown.
 */
export function InsightsHeader({
  days,
  onDaysChange,
  onRefresh,
  refreshing,
  showPeriod = true,
}: {
  days: number;
  onDaysChange: (days: number) => void;
  onRefresh: () => void;
  refreshing?: boolean;
  /** Whether the current section applies the reporting period. */
  showPeriod?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h2 className="text-[19px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
          Knowledge Analytics
        </h2>
        <p className="mt-[5px] text-[12.5px] leading-[1.5] text-[var(--co-ink-3)]">
          Insights into your knowledge base performance
        </p>
      </div>
      <div className="flex flex-none items-center gap-2">
        {showPeriod && (
          <ThemedSelect
            value={days}
            onChange={(e) => onDaysChange(Number(e.target.value))}
            aria-label="Reporting period"
            className="h-[34px] rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] focus:border-[var(--co-brass)] focus:outline-none"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </ThemedSelect>
        )}
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          aria-label="Refresh analytics"
          className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>
  );
}

/**
 * The section strip, copied from the screen it replaces.
 *
 * Same classes, so it renders identically in both themes: filled primary for
 * the active pill, grey for the rest. No counts — the original carried none,
 * and adding them here would make this the one part of the copy that is not a
 * copy.
 */
export function InsightsSectionTabs({
  active,
  onSelect,
}: {
  active: InsightsSection;
  onSelect: (id: InsightsSection) => void;
}) {
  return (
    <div className="flex gap-2 overflow-x-auto" role="tablist" aria-label="Insights sections">
      {SECTIONS.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={id === active}
          onClick={() => onSelect(id)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
            active === id
              ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200'
          }`}
        >
          <Icon className="h-4 w-4" />
          {label}
        </button>
      ))}
    </div>
  );
}

function Panel({
  title,
  blurb,
  children,
  foot,
}: {
  title: string;
  blurb: string;
  children: ReactNode;
  foot?: ReactNode;
}) {
  return (
    <div className={`${CARD_CLASS} flex min-w-0 flex-1 flex-col overflow-hidden`} style={CARD}>
      <div className="flex-none border-b border-[var(--co-line)] px-5 pb-[14px] pt-4">
        <h3 className="text-[19px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
          {title}
        </h3>
        <p className="mt-[5px] text-[12.5px] leading-[1.5] text-[var(--co-ink-3)]">{blurb}</p>
      </div>
      <div className="min-h-0 flex-1">{children}</div>
      {foot}
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="px-5 py-8 text-[12.5px] leading-[1.6] text-[var(--co-ink-3)]">{children}</p>
  );
}

// ---------------------------------------------------------------- 2. search

export function SearchAnalyticsSection({
  popular,
  failed,
  onDraft,
}: {
  popular: PopularSearch[];
  failed: FailedSearch[];
  onDraft: (query: string) => void;
}) {
  return (
    <div className="flex min-h-0 flex-1 gap-[14px]">
      <Panel
        title="What people search for"
        blurb="Ranked by volume. The click rate says whether the results were worth opening."
        foot={popular.length > 0 ? <CardFoot left="Most searched first" /> : undefined}
      >
        {popular.length === 0 ? (
          <Empty>No searches have been logged in this period.</Empty>
        ) : (
          popular.map((row) => {
            const ctr = clickRatePct(row);
            return (
              <div
                key={row.query}
                className="flex items-center gap-4 border-b border-[var(--co-line-subtle)] px-5 py-[13px] last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-semibold text-[var(--co-ink)]">
                    {`“${row.query}”`}
                  </div>
                  <div className={`${MONO} mt-1 text-[10px] uppercase tracking-[.08em] text-[var(--co-ink-4)]`}>
                    {`${row.count}× · avg ${row.avgResultCount ?? 0} results`}
                  </div>
                </div>
                {/* A high-volume query nobody clicks is the same failure as one
                    that returns nothing — it just fails one step later. */}
                <div className="flex-none text-right">
                  <div
                    className={`${MONO} text-[14px] font-semibold tabular-nums`}
                    style={{
                      color:
                        ctr === null
                          ? 'var(--co-ink-4)'
                          : ctr === 0
                            ? 'var(--co-burgundy)'
                            : 'var(--co-ink)',
                    }}
                  >
                    {ctr === null ? '—' : `${ctr}%`}
                  </div>
                  <div className={`${MONO} mt-[3px] text-[9px] uppercase tracking-[.1em] text-[var(--co-ink-4)]`}>
                    clicked
                  </div>
                </div>
              </div>
            );
          })
        )}
      </Panel>

      <Panel
        title="Searches that found nothing"
        blurb="Every one of these is a question your knowledge base was asked and could not answer."
        foot={failed.length > 0 ? <CardFoot left={`${failed.length} distinct queries`} /> : undefined}
      >
        {failed.length === 0 ? (
          <Empty>Every logged search returned something.</Empty>
        ) : (
          failed.map((row) => (
            <div
              key={row.query}
              className="flex items-center gap-4 border-b border-[var(--co-line-subtle)] px-5 py-[13px] last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold text-[var(--co-ink)]">
                  {`“${row.query}”`}
                </div>
                <div className={`${MONO} mt-1 truncate text-[10px] uppercase tracking-[.08em] text-[var(--co-terracotta)]`}>
                  {`asked ${row.count}× · zero results`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onDraft(row.query)}
                className={`${MONO} flex h-[30px] flex-none items-center whitespace-nowrap rounded-[7px] border border-[var(--co-brass-line)] bg-[var(--co-brass-wash)] px-3 text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--co-brass-ink)] hover:border-[var(--co-brass)]`}
              >
                Draft an article →
              </button>
            </div>
          ))
        )}
      </Panel>
    </div>
  );
}

// -------------------------------------------------------------- 3. articles

export function ArticlePerformanceSection({
  articles,
  onOpen,
}: {
  articles: ArticleEffectiveness[];
  onOpen: (id: string) => void;
}) {
  const ranked = [...articles].sort((a, b) => (b.viewCount ?? 0) - (a.viewCount ?? 0));

  return (
    <Panel
      title="How each article is performing"
      blurb="Views say it was found. Helpfulness says it answered the question — and this is the one screen that has the rating counts to show it honestly."
      foot={
        ranked.length > 0 ? (
          <CardFoot
            left={`${ranked.length} articles`}
            note="An article with no ratings is not a bad article — it is an unmeasured one."
          />
        ) : undefined
      }
    >
      {ranked.length === 0 ? (
        <Empty>No article performance has been recorded in this period.</Empty>
      ) : (
        <>
          <div
            className={`${MONO} flex items-center border-b border-[var(--co-line)] px-5 pb-[11px] pt-[14px] text-[9.5px] font-semibold uppercase tracking-[.2em] text-[var(--co-ink-4)]`}
          >
            <div className="min-w-0 flex-1">Article</div>
            <div className="w-[96px] flex-none text-right">Views</div>
            <div className="w-[168px] flex-none text-right">Helpfulness</div>
          </div>
          {ranked.map((row) => {
            const text = helpfulnessText(row);
            const unrated = text === 'No ratings';
            return (
              <button
                key={row.articleId}
                type="button"
                onClick={() => onOpen(row.articleId)}
                className="flex w-full items-center border-b border-[var(--co-line-subtle)] px-5 py-[13px] text-left last:border-b-0 hover:bg-[var(--co-selected)]"
              >
                <div className="min-w-0 flex-1 pr-4">
                  <div className="truncate text-[13px] font-semibold text-[var(--co-ink)]">
                    {row.title}
                  </div>
                </div>
                <div
                  className={`${MONO} w-[96px] flex-none text-right text-[13px] font-semibold tabular-nums text-[var(--co-ink)]`}
                >
                  {(row.viewCount ?? 0).toLocaleString()}
                </div>
                <div
                  className={`${MONO} w-[168px] flex-none text-right text-[12px] tabular-nums`}
                  style={{ color: unrated ? 'var(--co-ink-4)' : 'var(--co-ink-2)' }}
                >
                  {text}
                </div>
              </button>
            );
          })}
        </>
      )}
    </Panel>
  );
}

// ------------------------------------------------------------ 5. embeddings

export function EmbeddingsSection({
  statuses,
  onReindex,
  reindexing,
}: {
  statuses: EmbeddingStatus[];
  onReindex: (ids: string[]) => void;
  reindexing: boolean;
}) {
  const tally = embedTally(statuses);
  const health = embedHealth(statuses);
  const problems = embedPriority(statuses);

  return (
    <>
      {/* The original Embedding Health Status card set, restored.
          Four of these were here; TOTAL SOURCES and the health bar were not,
          and without the total the other figures had nothing to be a fraction
          of — four sources embedded reads very differently against nine than
          against four.

          EMBEDDED is the API's count: finished, stale ones included. The screen
          used to show `tally.embedded` (finished AND current), which is why it
          said 4 where the old panel said 9 — the same nine sources, five of
          which have drifted. Neither number was wrong; showing one of them
          without saying which was. The subtitle now says it outright. */}
      <div className="grid grid-cols-2 gap-[14px] md:grid-cols-5">
        <MetricCard
          title="Total Sources"
          value={health.totalSources}
          subtitle="Published articles and documents"
          icon={<Database className="h-[17px] w-[17px]" />}
          accent="machine"
        />
        <MetricCard
          title="Embedded"
          value={health.embeddedCount}
          subtitle={
            health.staleCount > 0
              ? `${health.staleCount} of these have drifted`
              : 'Serving current text'
          }
          icon={<CheckCircle2 className="h-[17px] w-[17px]" />}
          accent="green"
          note={`${tally.embedded} finished and still current; ${health.staleCount} finished but edited since.`}
        />
        <MetricCard
          title="Pending"
          value={health.pendingCount}
          subtitle="Queued or mid-run"
          icon={<Clock className="h-[17px] w-[17px]" />}
          accent={health.pendingCount > 0 ? 'terracotta' : 'machine'}
          note="The assistant cannot answer from these yet."
        />
        <MetricCard
          title="Stale"
          value={health.staleCount}
          subtitle="Counted inside Embedded"
          icon={<RefreshCw className="h-[17px] w-[17px]" />}
          accent={health.staleCount > 0 ? 'terracotta' : 'machine'}
          note="Edited since they were embedded. The assistant answers from the older text."
        />
        <MetricCard
          title="Failed"
          value={health.failedCount}
          subtitle="Will not retry on their own"
          icon={<AlertTriangle className="h-[17px] w-[17px]" />}
          accent={health.failedCount > 0 ? 'burgundy' : 'machine'}
        />
      </div>

      {/* Overall health. The same weighting the service applies — 70 embedded,
          20 free of failures, 10 fresh — computed from the rows above rather
          than fetched, so the bar can never disagree with the cards beside it. */}
      <div className={`${CARD_CLASS} px-[18px] py-4`} style={CARD}>
        <div className="flex items-baseline justify-between gap-4">
          <span
            className={`${MONO} text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]`}
          >
            Overall health
          </span>
          <span
            className={`${MONO} text-[15px] font-semibold tabular-nums`}
            style={{ color: healthTone(health.healthScore) }}
          >
            {`${Math.round(health.healthScore)}%`}
          </span>
        </div>
        <div
          className="mt-[10px] h-2 overflow-hidden rounded-[4px] bg-[var(--co-line-subtle)]"
          role="img"
          aria-label={`Overall embedding health ${Math.round(health.healthScore)} percent`}
        >
          <div
            className="h-full transition-[width]"
            style={{
              width: `${Math.max(0, Math.min(100, health.healthScore))}%`,
              background: healthTone(health.healthScore),
            }}
          />
        </div>
        <p className="mt-[9px] text-[11.5px] leading-[1.45] text-[var(--co-ink-3)]">
          70% of the score is how much is embedded, 20% is being free of failures, 10% is
          freshness — so a fully embedded base that has drifted cannot score above 90.
        </p>
      </div>

      <Panel
        title="Sources not serving current text"
        blurb="Counted from the per-source report rather than the dashboard summary, because the summary cannot name which source. The two do not disagree: a stale source is a completed one that has drifted, so it is counted in both."
        foot={
          problems.length > 0 ? (
            <CardFoot
              left={`${problems.length} of ${tally.total} sources need attention`}
              note="Failed first, then queued, then stale — stale still serves the old text, which is wrong but not silent."
            />
          ) : undefined
        }
      >
        {problems.length === 0 ? (
          <Empty>
            {tally.total === 0
              ? 'No embedding report was returned for this tenant.'
              : `All ${tally.total} sources are embedded and current.`}
          </Empty>
        ) : (
          problems.map((row) => (
            <div
              key={`${row.sourceType}-${row.sourceId}`}
              className="flex items-center gap-4 border-b border-[var(--co-line-subtle)] px-5 py-[13px] last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                {/* The title, not the UUID. `title` has always been in this
                    payload; the id was on screen only because the type did
                    not declare the field. */}
                <div className="truncate text-[13px] font-semibold text-[var(--co-ink)]">
                  {row.title || row.sourceId}
                </div>
                <div
                  className={`${MONO} mt-1 truncate text-[10px] uppercase tracking-[.08em]`}
                  style={{
                    color:
                      embedState(row) === 'failed'
                        ? 'var(--co-burgundy)'
                        : 'var(--co-terracotta-ink)',
                  }}
                >
                  {[
                    row.sourceType,
                    embedState(row),
                    row.chunkCount > 0 ? `${row.chunkCount} chunks` : 'no chunks',
                  ].join(' · ')}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onReindex([row.sourceId])}
                disabled={reindexing}
                className={`${MONO} flex h-[30px] flex-none items-center whitespace-nowrap rounded-[7px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] disabled:opacity-50`}
              >
                {reindexing ? 'Queueing…' : 'Re-embed'}
              </button>
            </div>
          ))
        )}
      </Panel>
    </>
  );
}
